"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";

import type { singleSelectFieldSchema } from "@/app/directories/checkout/components/intake/IntakeFormTypes";
import { getTimezoneAbbreviation } from "@frontend/lib/timezone/timezone-utils";
import { Button } from "@frontend/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@frontend/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { cn } from "@frontend/shared/lib/utils";
import { CommandList } from "cmdk";
import type { z } from "zod";

export function BasicDropdown({
  data,
  onSelectValue,
  labelText,
  valuePlaceholderText,
  valueText,
  selectedValue,
  classNames,
  labelClassName,
  modal,
  pdfquestion,
  disabled,
  commandGroupClassName,
}: {
  data: { value: string; label: string; [x: string]: unknown }[];
  onSelectValue: (datum: {
    value: string;
    label: string;
    [x: string]: unknown;
  }) => void;
  labelText?: string | React.ReactNode;
  valuePlaceholderText: string;
  valueText: string;
  selectedValue: { value: string; label: string } | undefined;
  classNames?: string;
  labelClassName?: string;
  modal?: boolean;
  pdfquestion?: z.infer<typeof singleSelectFieldSchema>;
  disabled?: boolean;
  commandGroupClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover modal={modal} open={open} onOpenChange={setOpen}>
      {labelText && (
        <p
          className={cn("text-sm font-normal", labelClassName)}
          data-pdflabel="multiInput-label"
          data-questionid={pdfquestion ? pdfquestion.id : ""}
        >
          {labelText}
        </p>
      )}
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          aria-expanded={open}
          className={cn(
            "w-full justify-between text-sm font-normal text-left overflow-hidden",
            !selectedValue && "text-gray-500",
            classNames,
          )}
        >
          <div
            className="w-5/6 overflow-hidden text-ellipsis"
            data-pdflabel="singleInput-choice"
            data-questionid={pdfquestion ? pdfquestion.id : ""}
            data-choiceindex={
              selectedValue
                ? data.findIndex((datum) => datum.value === selectedValue.value)
                : 0
            }
          >
            {selectedValue
              ? data.find((datum) => datum.value === selectedValue.value)?.label
              : valuePlaceholderText}
          </div>

          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent modal={modal} className={cn("p-0", classNames)}>
        <Command>
          <CommandInput />
          <CommandList>
            <CommandEmpty>No {valueText} found.</CommandEmpty>
            <CommandGroup
              className={cn(
                "overflow-y-scroll max-h-96",
                commandGroupClassName,
              )}
            >
              {data.map((datum) => (
                <CommandItem
                  key={datum.label}
                  value={datum.label}
                  onSelect={(currentValue) => {
                    if (disabled) {
                      return;
                    }
                    const isValueUnselected =
                      currentValue === selectedValue?.label;
                    onSelectValue(
                      isValueUnselected ? { label: "", value: "" } : datum,
                    );
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedValue?.value === datum.value
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <div className="flex items-center justify-between w-full">
                    {datum.label}
                    {typeof datum?.timeZone === "string" && (
                      <span className="text-xs text-gray-500">
                        {getTimezoneAbbreviation(datum.timeZone)}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
